// src/utils/apolloQueries.js
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { consoleDebug } from '../util';
import * as Sentry from '@sentry/react';

// Fetch policy configurations for different query types
export const FETCH_POLICIES = {
    MENU: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    },
    PRODUCT: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only',
        notifyOnNetworkStatusChange: true,
    },
    SPECIALS: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    },
    RETAILER: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    },
    CART: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only',
        errorPolicy: 'all',
    }
};

// Enhanced error tracking for Apollo queries
const trackApolloQueryError = (error, operationName, variables, context = {}) => {
    // Only track critical errors to avoid noise
    const isCriticalError = error?.networkError || 
        error?.graphQLErrors?.some(err => 
            err.extensions?.code === 'INTERNAL_ERROR' || 
            err.message?.includes('Load failed') ||
            err.message?.includes('Network error')
        );

    if (isCriticalError) {
        Sentry.withScope((scope) => {
            scope.setLevel('error');
            scope.setTag('error_type', 'apollo_query_hook_error');
            scope.setTag('operation_name', operationName || 'unknown');
            scope.setTag('hook_context', context.hookName || 'unknown_hook');
            
            // Add query context
            scope.setContext('apollo_query_context', {
                operationName,
                variables: variables ? JSON.stringify(variables, null, 2) : 'none',
                hookName: context.hookName,
                componentContext: context.componentContext,
                queryType: context.queryType
            });

            // Add error details
            if (error.networkError) {
                scope.setContext('network_error_details', {
                    message: error.networkError.message,
                    statusCode: error.networkError.statusCode,
                    bodyText: error.networkError.bodyText
                });
                scope.setTag('network_error_status', error.networkError.statusCode);
            }

            if (error.graphQLErrors?.length > 0) {
                scope.setContext('graphql_error_details', {
                    errors: error.graphQLErrors.map(err => ({
                        message: err.message,
                        path: err.path,
                        extensions: err.extensions
                    }))
                });
                scope.setTag('graphql_error_code', error.graphQLErrors[0].extensions?.code);
            }

            scope.setFingerprint([
                'apollo_query_hook_error',
                operationName || 'unknown_operation',
                context.hookName || 'unknown_hook',
                error.networkError ? 'network' : 'graphql'
            ]);

            Sentry.captureException(error);
        });
    }
};

// Custom hook for menu products with Apollo
export const useApolloMenuProducts = (query, variables, options = {}) => {
    const { 
        fetchPolicy = FETCH_POLICIES.MENU,
        onCompleted,
        onError,
        skip = false,
        ...otherOptions 
    } = options;

    const operationName = query?.definitions?.[0]?.name?.value || 'MenuQuery';

    const queryResult = useQuery(query, {
        variables,
        skip,
        ...fetchPolicy,
        onCompleted: (data) => {
            consoleDebug('[Apollo Menu Query] Completed:', {
                operationName,
                variables,
                dataReceived: !!data,
                productsCount: data?.menu?.productsCount
            });
            onCompleted?.(data);
        },
        onError: (error) => {
            console.error('[Apollo Menu Query] Error:', error);
            
            // Track error with enhanced context
            trackApolloQueryError(error, operationName, variables, {
                hookName: 'useApolloMenuProducts',
                queryType: 'menu',
                componentContext: 'menu_products_grid'
            });
            
            onError?.(error);
        },
        ...otherOptions
    });

    return queryResult;
};

// Custom hook for cart/checkout with optimistic cache updates
export const useApolloCart = (query, variables, options = {}) => {
    const operationName = query?.definitions?.[0]?.name?.value || 'CartQuery';
    
    const queryResult = useQuery(query, {
        variables,
        ...FETCH_POLICIES.CART,
        onError: (error) => {
            console.error('[Apollo Cart Query] Error:', error);
            
            trackApolloQueryError(error, operationName, variables, {
                hookName: 'useApolloCart',
                queryType: 'cart',
                componentContext: 'checkout_cart'
            });
        },
        ...options
    });

    // Ensure cart data stays fresh
    useEffect(() => {
        const interval = setInterval(() => {
            if (!document.hidden) {
                queryResult.refetch().catch(error => {
                    console.warn('Cart refetch error:', error);
                    // Don't track periodic refetch errors unless they're critical
                    if (error?.networkError?.statusCode >= 500) {
                        trackApolloQueryError(error, operationName, variables, {
                            hookName: 'useApolloCart',
                            queryType: 'cart_refetch',
                            componentContext: 'periodic_update'
                        });
                    }
                });
            }
        }, 60000); // Refetch every minute when tab is active

        return () => clearInterval(interval);
    }, [queryResult.refetch, operationName, variables]);

    return queryResult;
};

// Enhanced useQuery wrapper with automatic error tracking
export const useTrackedQuery = (query, options = {}) => {
    const operationName = query?.definitions?.[0]?.name?.value || 'UnknownQuery';
    const { variables, context, ...queryOptions } = options;

    return useQuery(query, {
        variables,
        onError: (error) => {
            trackApolloQueryError(error, operationName, variables, {
                hookName: 'useTrackedQuery',
                queryType: context?.queryType || 'unknown',
                componentContext: context?.component || 'unknown'
            });
        },
        ...queryOptions
    });
};

// Utility for cache updates after mutations
export const updateCacheAfterMutation = (cache, { query, variables, data, updateFn }) => {
    try {
        const existingData = cache.readQuery({
            query,
            variables
        });

        if (existingData) {
            const updatedData = updateFn(existingData, data);
            
            cache.writeQuery({
                query,
                variables,
                data: updatedData
            });
        }
    } catch (error) {
        console.error('Cache update error:', error);
        
        // Track cache update errors
        Sentry.withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('error_type', 'apollo_cache_update_error');
            scope.setContext('cache_update_context', {
                queryName: query?.definitions?.[0]?.name?.value,
                variables: variables ? JSON.stringify(variables) : 'none'
            });
            Sentry.captureException(error);
        });
    }
};

// Helper to invalidate specific queries
export const invalidateQueries = (client, queryNames = []) => {
    try {
        queryNames.forEach(queryName => {
            client.cache.evict({
                id: 'ROOT_QUERY',
                fieldName: queryName
            });
        });
        client.cache.gc();
    } catch (error) {
        console.error('Query invalidation error:', error);
        
        Sentry.withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('error_type', 'apollo_cache_invalidation_error');
            scope.setContext('invalidation_context', {
                queryNames
            });
            Sentry.captureException(error);
        });
    }
};

// Prefetch utility for critical data
export const prefetchCriticalData = async (client, retailerId) => {
    const queries = [
        {
            query: gql`
                query RetailerQuery($retailerId: ID!) {
                    retailer(id: $retailerId) {
                        id
                        name
                        address
                        hours
                        menuType
                    }
                }
            `,
            variables: { retailerId }
        }
    ];

    const results = await Promise.allSettled(
        queries.map(({ query, variables }) =>
            client.query({
                query,
                variables,
                fetchPolicy: 'network-only'
            })
        )
    );

    // Track any prefetch failures
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            const queryName = queries[index].query?.definitions?.[0]?.name?.value;
            
            trackApolloQueryError(result.reason, queryName, queries[index].variables, {
                hookName: 'prefetchCriticalData',
                queryType: 'prefetch',
                componentContext: 'app_initialization'
            });
        }
    });

    return results;
};
